/**
 * The console's door (1.48 „მცველი", migrations 166/167).
 *
 * `platform_admin_gate()` tells the signed-in user what /platform must ask
 * before it opens: nothing for a shop user ('none' — the console is not
 * theirs), the phone's QR the first time ('enroll'), the 6-digit code on a
 * session that signed in with the password only ('verify'), or nothing more
 * ('ok', aal2). Since 167 the database refuses every platform power below
 * 'ok' on its own; this file only decides which card the screen shows and
 * what it says.
 *
 * Why the code is asked on /platform and not at sign-in: GoTrue logs out
 * every OTHER aal1 session of a user on each successful code. The console
 * login is its own account (the owner, 2026-10-16) so that no shop's till
 * ever shares it.
 */

export type PlatformGate = 'none' | 'enroll' | 'verify' | 'ok'

const GATES: readonly PlatformGate[] = ['none', 'enroll', 'verify', 'ok']

/** Anything the server did not say exactly — an error, `true` from the old
 *  is_platform_admin(), a typo — is 'none': it never opens the console. */
export function parseGate(data: unknown): PlatformGate {
  return typeof data === 'string' && (GATES as readonly string[]).includes(data) ? (data as PlatformGate) : 'none'
}

/** What /platform shows for the gate RPC's answer. 42501 is a signed-out
 *  visitor (anon may not call the gate) — the plain „no access" page. Any
 *  other failure means we do not know: 'error', a retry — never the console. */
export function gateFromRpc(r: { data: unknown; error: { code?: string; message?: string } | null }): PlatformGate | 'error' {
  if (r.error) return r.error.code === '42501' ? 'none' : 'error'
  return parseGate(r.data)
}

/** An authenticator code: exactly six ASCII digits. */
export function isTotpCode(s: string): boolean {
  return /^[0-9]{6}$/.test(s)
}

/** What the code field keeps of what was typed or pasted. */
export function totpDigits(s: string): string {
  return s.replace(/[^0-9]/g, '').slice(0, 6)
}

/** GoTrue's refusals in the owner's words. `code` is GoTrue's error code
 *  (supabase-js AuthError.code), `status` its HTTP status. */
export function mfaErrorText(err: { code?: string; status?: number; message?: string } | null | undefined): string {
  if (err?.status === 429 || err?.code === 'over_request_rate_limit') return 'ძალიან ბევრი ცდა — მოიცადე ერთი წუთი'
  switch (err?.code) {
    case 'mfa_verification_failed': return 'კოდი არასწორია — შეიყვანე ტელეფონზე ახლა ნაჩვენები კოდი'
    case 'mfa_challenge_expired': return 'კოდს ვადა გაუვიდა — შეიყვანე ახალი'
    case 'insufficient_aal': return 'ჯერ ტელეფონის კოდით შედი'
    default: return 'ვერ მოხერხდა — სცადე თავიდან'
  }
}

/** The ids of TOTP factors an abandoned enrolment left behind. They are
 *  removed before a new one: GoTrue counts them against the factor limit,
 *  and verifying the new one would otherwise leave them lying about. */
export function staleUnverified(factors: { id: string; factor_type: string; status: string }[]): string[] {
  return factors.filter((f) => f.factor_type === 'totp' && f.status === 'unverified').map((f) => f.id)
}

/** The QR as an <img> src. supabase-js hands GoTrue's SVG back as
 *  `data:image/svg+xml;utf-8,<svg …>` — unencoded, so a `#` in it (a colour)
 *  ends the URL there and the image breaks. The SVG is re-encoded; a URL
 *  that is already usable is left alone. */
export function qrSrc(qr: string): string {
  const svgAt = qr.indexOf('<svg')
  if (svgAt === -1) return qr
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qr.slice(svgAt))}`
}

/** A friendly name per attempt — GoTrue refuses a name the user already has. */
export function enrollName(now: Date): string {
  return `MartePOS · ${now.toISOString().slice(0, 19)}`
}
